import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { COLORS, ANIM, EASING } from '../constants/theme';

const products = [
  {
    id: 'pulse',
    href: '/pulse',
    label: 'Live',
    labelColor: '#10b981',
    labelBg: 'rgba(16,185,129,0.1)',
    labelBorder: 'rgba(16,185,129,0.3)',
    accent: COLORS.blue,
    accentB: '#22D3EE',
    tags: ['AI Chatbot', 'No-code Setup', '24/7 Support', 'Lead Capture'],
    name: 'Pulse',
    tagline: 'AI Support Agent for Your Business',
    desc: 'Train an AI agent on your docs, FAQs, and workflows. Deploy a support chatbot that truly understands your business — resolves queries, captures leads, and hands off to humans when needed. No code required.',
  },
  {
    id: 'coming-soon',
    href: null,
    label: 'Coming Soon',
    labelColor: '#8b5cf6',
    labelBg: 'rgba(139,92,246,0.08)',
    labelBorder: 'rgba(139,92,246,0.25)',
    accent: '#8b5cf6',
    accentB: '#6366f1',
    tags: [],
    name: 'More Products',
    tagline: "We're building more",
    desc: "New AI-powered products are in development. Follow us to stay updated on what's coming next from Reano AI Technologies.\nHave any specific requirements? Get in touch for custom solutions.",
  },
];

function PulseWaveIcon({ size = 22, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <polyline points="4,16 8,16 10,9 13,23 16,12 19,20 22,16 28,16"
        stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ComingSoonIcon({ size = 22, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default function Products() {
  return (
    <section id="products" style={{ padding: '96px 0', background: '#fff' }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, ease: EASING.spring }}
          style={{ marginBottom: 56, maxWidth: 560 }}
        >
          <div className="section-eyebrow">Our Products</div>
          <h2 className="section-title" style={{ marginBottom: 14 }}>AI tools built for real businesses</h2>
          <p className="section-sub">
            Products designed to automate support, capture leads, and put AI to work — without the complexity.
          </p>
        </motion.div>

        <motion.div
          variants={ANIM.stagger} initial="hidden" whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}
        >
          {products.map(p => (
            <motion.div
              key={p.id}
              variants={ANIM.staggerItem}
              className="card"
              style={{ padding: 32, borderTop: `3px solid ${p.accent}`, display: 'flex', flexDirection: 'column' }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                  background: `linear-gradient(135deg,${p.accent},${p.accentB})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 4px 16px ${p.accent}33`,
                }}>
                  {p.id === 'pulse' ? <PulseWaveIcon /> : <ComingSoonIcon />}
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: p.labelColor, background: p.labelBg,
                  border: `1px solid ${p.labelBorder}`,
                  borderRadius: 6, padding: '4px 10px',
                }}>
                  {p.label}
                </span>
              </div>

              {/* Text */}
              <h3 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.03em', color: COLORS.text, marginBottom: 4 }}>{p.name}</h3>
              <p style={{ fontSize: 13, fontWeight: 600, color: p.accent, marginBottom: 12, letterSpacing: '-.01em' }}>{p.tagline}</p>
              <p style={{ fontSize: 14.5, color: COLORS.text2, lineHeight: 1.65, flex: 1, whiteSpace: 'pre-line' }}>{p.desc}</p>

              {/* Tags */}
              {p.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '20px 0' }}>
                  {p.tags.map(tag => (
                    <span key={tag} style={{
                      fontSize: 12, fontWeight: 500, color: COLORS.text2,
                      background: '#F0F4F8', border: `1px solid ${COLORS.border}`,
                      borderRadius: 6, padding: '4px 10px',
                    }}>{tag}</span>
                  ))}
                </div>
              )}

              {/* CTA */}
              {p.href ? (
                <Link
                  to={p.href}
                  style={{
                    marginTop: p.tags.length ? 0 : 24,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 14, fontWeight: 700, color: p.accent,
                    textDecoration: 'none',
                    transition: 'gap 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.gap = '10px'; }}
                  onMouseLeave={e => { e.currentTarget.style.gap = '6px'; }}
                >
                  Explore Pulse
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 7h10M7.5 3l4 4-4 4" />
                  </svg>
                </Link>
              ) : (
                <a href="#contact"
                  style={{
                    marginTop: 24,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 14, fontWeight: 700, color: '#8b5cf6',
                    textDecoration: 'none', transition: 'gap 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.gap = '10px'; }}
                  onMouseLeave={e => { e.currentTarget.style.gap = '6px'; }}
                >
                  Get in touch for custom solutions
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 7h10M7.5 3l4 4-4 4" />
                  </svg>
                </a>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
